import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { OpCard } from 'new-components/DLS/OpCard/OpCard';
import { OpAvatar } from 'new-components/DLS/OpAvatar/OpAvatar';
import { OpText } from 'new-components/DLS/OpText/OpText';
import { OpSpace } from 'new-components/DLS/OpSpace/OpSpace';
import { OpButton } from 'new-components/DLS/OpButton/OpButton';

import './BlurbCard.scss';

const { Meta } = OpCard;

interface BlurbCardProps {
  classes?: Partial<{
    root: string;
    cover: string;
    avatar: string;
    meta: string;
    content: string;
    buttons: string;
    learnMore: string;
  }>;
  coverImage?: string;
  title: ReactNode;
  avatar?: ReactNode;
  content: ReactNode;
  externalLink?: string;
  externalLinkText?: string;
  titleBar?: boolean;
  ctaButton?: ReactNode;
  externalLinkButtonTestId?: string;
}

export const BlurbCard = ({
  classes,
  coverImage,
  title,
  avatar,
  content,
  externalLink,
  externalLinkText,
  titleBar,
  ctaButton,
  externalLinkButtonTestId,
}: BlurbCardProps) => {
  const { t } = useTranslation();

  const showFooter = !!externalLink || !!ctaButton;

  return (
    <OpCard
      className={clsx('blurb-card', classes?.root)}
      title={titleBar && title}
      cover={
        !!coverImage && (
          <img
            alt="Blurb cover"
            src={coverImage}
            className={clsx('blurb-card__cover', classes?.cover)}
          />
        )
      }
    >
      {!!avatar && (
        <OpAvatar
          src={avatar}
          shape="square"
          size="large"
          className={clsx('blurb-card__avatar', classes?.avatar)}
        />
      )}
      {!titleBar && (
        <Meta
          className={clsx('blurb-card__meta', classes?.meta, {
            'blurb-card__meta--with-avatar': !!avatar,
          })}
          title={title}
        />
      )}

      <OpSpace orientation="vertical" size="middle">
        {Array.isArray(content) ? (
          content.map((item, i) => (
            <div
              className={clsx('blurb-card__content', classes?.content)}
              key={i}
            >
              {typeof item === 'string' ? (
                <OpText size="small">{item}</OpText>
              ) : (
                item
              )}
            </div>
          ))
        ) : (
          <div className={clsx('blurb-card__content', classes?.content)}>
            {typeof content === 'string' ? (
              <OpText size="small">{content}</OpText>
            ) : (
              content
            )}
          </div>
        )}
      </OpSpace>
      {showFooter && (
        <OpSpace
          className={clsx('blurb-card__buttons', classes?.buttons)}
          size="large"
        >
          {externalLink && (
            <div className={classes?.learnMore}>
              <OpButton
                href={externalLink}
                {...(!ctaButton && { type: 'primary' })}
                {...(externalLinkButtonTestId && {
                  'data-testid': `${externalLinkButtonTestId}-learn-more-btn`,
                })}
              >
                {externalLinkText || t('Learn more')}
              </OpButton>
            </div>
          )}
          {ctaButton}
        </OpSpace>
      )}
    </OpCard>
  );
};
